import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createAuthenticatedAxiosInstance } from "../auth/auth.slice";

const initialState = {
  customers: [],
  customer: null,
  success: false,
  isLoading: false,
  error: null,
  message: null,
};

export const addCustomer = createAsyncThunk(
  "customer/addCustomer",
  async (data, { rejectWithValue, getState }) => {
    try {
      const axiosInstance = createAuthenticatedAxiosInstance();
      const response = await axiosInstance.post("customers", data);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to add customer",
        error: error.response?.data || error.message,
      });
    }
  }
);

export const getCustomers = createAsyncThunk(
  "customer/getCustomers",
  async (_, { rejectWithValue, getState }) => {
    try {
      const axiosInstance = createAuthenticatedAxiosInstance();
      const response = await axiosInstance.get("customers");
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to fetch customers",
        error: error.response?.data || error.message,
      });
    }
  }
);

export const getCustomer = createAsyncThunk(
  "customer/getCustomer",
  async (id, { rejectWithValue, getState }) => {
    try {
      const axiosInstance = createAuthenticatedAxiosInstance();
      const response = await axiosInstance.get(`customers/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateCustomer = createAsyncThunk(
  "customer/updateCustomer",
  async (data, { rejectWithValue, getState }) => {
    try {
      const axiosInstance = createAuthenticatedAxiosInstance();
      const response = await axiosInstance.put(`customers/${data._id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to update customer",
        error: error.response?.data || error.message,
      });
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  "customer/deleteCustomer",
  async (id, { rejectWithValue, getState }) => {
    try {
      const axiosInstance = createAuthenticatedAxiosInstance();
      const response = await axiosInstance.delete(`customers/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to delete customer",
        error: error.response?.data || error.message,
      });
    }
  }
);

export const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.error = null;
      state.success = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Customers
      .addCase(getCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.customers = action.payload.data || [];
        state.error = null;
      })
      .addCase(getCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to fetch customers";
      })
      // Get Customer
      .addCase(getCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.customer = action.payload.data || null;
        state.error = null;
      })
      .addCase(getCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to fetch customer";
      })
      // Add Customer
      .addCase(addCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message =
          action.payload?.message || "Customer added successfully";
        state.error = null;
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to add customer";
      })
      // Update Customer
      .addCase(updateCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message =
          action.payload?.message || "Customer updated successfully";
        state.error = null;
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to update customer";
      })
      // Delete Customer
      .addCase(deleteCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.message =
          action.payload?.message || "Customer deleted successfully";
        state.error = null;
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to delete customer";
      });
  },
});

export const { reset } = customerSlice.actions;
export default customerSlice.reducer;
